import { Button, Dialog, DialogDescription, DialogHeader, Grid, GridRow, ToastLevel, useIntl, useToast } from "@metsooutotec/modes-react-components"
import { FC, useState } from "react"
import sensorStore from "features/iot/sensors/stores/sensor-store"
import { GenerateSensorsDto } from "features/iot/sensors/models/sensor-models"
import { NavigationModalOptions } from "components/utils/modal-helper"
import { validateDecimal, validateHours, validateInteger, validateText } from "validators/inputValidators"
import { StoreResults } from "stores/stores-helper"
import SensorInputFields from "../../utils/sensor-input-fields"
import { useGenerateSensorForm } from "./use-generate-sensor-form"

interface GenerateSensorsModalProps {
    isOpen: boolean | undefined
    onClose: (passed: boolean, customerId?: string, navigationSensorBatchOptions?: NavigationModalOptions) => void
    sensorBatchId: string
}
interface SensorFormField {
    id: string
    label: string
    placeholder: string
    type: string
    validate?: (value: string) => string
    inputMode?: string
    pattern?: string
    onFileUpload?: () => void
    unSelectFile?: () => void
    isRequired: boolean
}
const GenerateSensorsModal: FC<GenerateSensorsModalProps> = ({ isOpen, onClose, sensorBatchId }) => {
    const { formatMessage } = useIntl()
    const { setMessage } = useToast()
    const { formData, formErrors, handleChange, handleValidation, resetForm, validateFormFields } = useGenerateSensorForm()

    const [isLoading, setLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<{ isError: boolean; errorMessage: string }>({ isError: false, errorMessage: "" })

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault()
        try {
            setLoading(true)
            // Validate the form
            const isValid = validateFormFields()

            if (!isValid) {
                setIsError({ isError: true, errorMessage: "There are validation errors, update the form texts" })
                setLoading(false)
                return
            }

            setIsError({ isError: false, errorMessage: "" })

            const { serialNumberPrefix, numberOfSensors, offset, numberOfResistiveElements, pitch, hardwareRevision, firmwareRevision } = formData

            const generateSensorsRange: GenerateSensorsDto = {
                sensorBatchId,
                serialNumberPrefix,
                numberOfSensors: parseInt(numberOfSensors),
                offset: parseInt(offset),
                numberOfResistiveElements: parseInt(numberOfResistiveElements),
                pitch: parseInt(pitch),
                hardwareRevision,
                firmwareRevision,
            }
            const requestResult: StoreResults = await sensorStore.generateSensors(generateSensorsRange)

            if (!requestResult.passed) {
                setIsError({ isError: true, errorMessage: requestResult.message })
                setLoading(false)
                return
            }
            //Handle success
            resetForm() // Reset form data and errors
            if (requestResult.message) {
                setMessage(requestResult.message, ToastLevel.WARNING, 6000)
            } else {
                setMessage(`Sensors have been Created!`, ToastLevel.SUCCESS, 6000)
            }
            onClose(false)
        } catch (error) {
            setIsError({ isError: true, errorMessage: "Form submission error, please read logs" })
            console.error("Form submission error:", error)
        } finally {
            setLoading(false)
        }
    }
    const onCloseProcess = (): void => {
        resetForm()
        setIsError({ isError: false, errorMessage: "" })
        onClose(false)
    }
    const labels = {
        confirm: formatMessage("modal.confirm"),
        cancel: formatMessage("modal.cancel"),
        header: formatMessage("sensorsModal.generateSensors"),
        description: formatMessage("sensorsModal.generateSensorsDescription"),
        serialNumberPrefix: formatMessage("sensorsModal.serialNumberPrefix"),
        numberOfSensors: formatMessage("sensorsModal.numberOfSensors"),
    }
    const inputStrings: SensorFormField[] = [
        {
            id: "serialNumberPrefix",
            label: formatMessage("sensorsModal.serialNumberPrefix"),
            placeholder: `Type ${labels.serialNumberPrefix} here...`,
            type: "string",
            isRequired: true,
            validate: validateText,
        },
        {
            id: "numberOfSensors",
            label: labels.numberOfSensors,
            placeholder: `Type ${labels.numberOfSensors} here...`,
            type: "number",
            isRequired: true,
            validate: validateDecimal,
        },
        {
            id: "offset",
            label: formatMessage("sensorsModal.offset"),
            placeholder: `${formatMessage("keyWord.Enter")} ${formatMessage("sensorsModal.offset")}`,
            type: "text",
            isRequired: true,
            validate: validateHours,
        },
        {
            id: "numberOfResistiveElements",
            label: formatMessage("sensorsModal.numberOfResistiveElements"),
            placeholder: `${formatMessage("keyWord.Enter")} ${formatMessage("sensorsModal.numberOfResistiveElements")}`,
            type: "text",
            isRequired: true,
            validate: validateInteger,
        },
        {
            id: "pitch",
            label: formatMessage("sensorsModal.pitch"),
            placeholder: `${formatMessage("keyWord.Enter")} ${formatMessage("sensorsModal.pitch")}`,
            type: "text",
            isRequired: true,
            validate: validateDecimal,
        },
        {
            id: "hardwareRevision",
            label: formatMessage("sensorsModal.hardwareRevision"),
            placeholder: `${formatMessage("keyWord.Enter")} ${formatMessage("sensorsModal.hardwareRevision")}`,
            type: "text",
            isRequired: true,
            validate: validateText,
        },
        {
            id: "firmwareRevision",
            label: formatMessage("sensorsModal.firmwareRevision"),
            placeholder: `${formatMessage("keyWord.Enter")} ${formatMessage("sensorsModal.firmwareRevision")}`,
            type: "text",
            isRequired: true,
            validate: validateText,
        },
    ]
    const renderButtons = (): React.ReactNode => {
        const buttonsConfig = [
            { label: labels.confirm, onClick: handleSubmit, primary: true },
            { label: labels.cancel, onClick: onCloseProcess, primary: false },
        ]
        return buttonsConfig.map((button, index) => {
            const { label, onClick, primary } = button
            const buttonGridStyle =
                index === 0
                    ? {
                          marginBottom: "var(--size-m)",
                      }
                    : {}
            return (
                <GridRow key={label} style={buttonGridStyle}>
                    <Button
                        loading={isLoading}
                        disabled={isLoading}
                        onClick={onClick}
                        flexible={true}
                        children={label}
                        centered
                        size={"medium"}
                        primary={primary}
                    />
                </GridRow>
            )
        })
    }
    return (
        <Dialog dimmer open={isOpen} size={"small"}>
            <DialogHeader username={""}>{labels.header}</DialogHeader>
            {isError.isError && <p style={{ color: "red" }}>{isError.errorMessage}</p>}
            <DialogDescription style={{ marginBottom: "var(--size-xxl)" }}>{labels.description}</DialogDescription>
            <Grid columns={2}>
                <SensorInputFields
                    formData={formData}
                    formErrors={formErrors}
                    handleChange={handleChange}
                    handleValidation={handleValidation}
                    onClearClick={inputId => handleChange(inputId, "")}
                    inputStrings={inputStrings}
                />
                {renderButtons()}
            </Grid>
        </Dialog>
    )
}

export default GenerateSensorsModal
